import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Input, Upload, Form, Alert, Tag, Space, Spin, Empty } from 'antd';
import { AndroidOutlined, AppleOutlined, UploadOutlined, SaveOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { etatPlateforme, formaterTaille } from '../application-mobile/etatPublication';
import { can } from '../../variables/constants';
import { HAB } from '../../variables/habilitations';

const VERT = '#1a6b3c';

export default function PublierApplication() {
  const { t } = useTranslation('parametres');
  const [liste, setListe] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [fichier, setFichier] = useState(null);
  const [envoiAndroid, setEnvoiAndroid] = useState(false);
  const [envoiIos, setEnvoiIos] = useState(false);
  const [formAndroid] = Form.useForm();
  const [formIos] = Form.useForm();

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const r = await axiosInstance.get('/v1/application-mobile');
      setListe(r.data?.data || []);
    } catch { setListe([]); }
    setChargement(false);
  }, []);

  useEffect(() => { charger(); }, [charger]);

  if (!can(HAB.APP_MOBILE_PUBLIER)) {
    return (
      <Layout title={t('applicationMobile.page.titreOnglet')}>
        <Empty description={t('applicationMobile.page.habilitationRequise')} />
      </Layout>
    );
  }

  const android = etatPlateforme(liste, 'ANDROID');
  const ios = etatPlateforme(liste, 'IOS');

  const publierAndroid = async (v) => {
    if (!fichier) { toast.error(t('applicationMobile.messages.selectionnerApk')); return; }
    setEnvoiAndroid(true);
    try {
      const fd = new FormData();
      fd.append('fichier', fichier);
      await axiosInstance.post(
        `/v1/application-mobile/android?version=${encodeURIComponent(v.version)}`
          + (v.notes ? `&notes=${encodeURIComponent(v.notes)}` : ''),
        fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('applicationMobile.messages.androidPubliee'));
      setFichier(null);
      formAndroid.resetFields();
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('applicationMobile.messages.publicationImpossible'));
    }
    setEnvoiAndroid(false);
  };

  const publierIos = async (v) => {
    setEnvoiIos(true);
    try {
      await axiosInstance.put('/v1/application-mobile/ios', {
        url: v.url, version: v.version, notes: v.notes || null,
      });
      toast.success(t('applicationMobile.messages.iosPublie'));
      formIos.resetFields();
      charger();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('applicationMobile.messages.publicationImpossible'));
    }
    setEnvoiIos(false);
  };

  const Courante = ({ etat, suffixe }) => (
    etat.publie ? (
      <Space wrap style={{ marginBottom: 12 }}>
        <Tag color="green">{t('applicationMobile.courante.version', { version: etat.version })}</Tag>
        <Tag>{t('applicationMobile.courante.publieeLe', { date: dayjs(etat.datePublication).format('DD/MM/YYYY') })}</Tag>
        {suffixe}
      </Space>
    ) : (
      <Tag style={{ marginBottom: 12 }}>{t('applicationMobile.courante.aucuneVersion')}</Tag>
    )
  );

  if (chargement) return <Layout title={t('applicationMobile.page.titreOnglet')}>
    <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div></Layout>;

  return (
    <Layout title={t('applicationMobile.page.titreOnglet')}>
      <Alert type="warning" showIcon style={{ marginBottom: 16 }}
        message={t('applicationMobile.avertissement.titre')}
        description={t('applicationMobile.avertissement.description')} />

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <Card className="cb-card" style={{ flex: 1, minWidth: 360 }}
          title={<Space><AndroidOutlined style={{ color: VERT }} />Android</Space>}>
          <Courante etat={android}
            suffixe={android.nomFichier && <Tag>{android.nomFichier} · {formaterTaille(android.tailleOctets)}</Tag>} />
          <Form form={formAndroid} layout="vertical" onFinish={publierAndroid}>
            <Form.Item label={t('applicationMobile.formulaire.fichierApk')} required>
              <Upload beforeUpload={(f) => { setFichier(f); return false; }}
                maxCount={1} accept=".apk"
                fileList={fichier ? [{ uid: '1', name: fichier.name }] : []}
                onRemove={() => setFichier(null)}>
                <Button icon={<UploadOutlined />}>{t('applicationMobile.formulaire.selectionnerApk')}</Button>
              </Upload>
            </Form.Item>
            <Form.Item name="version" label={t('applicationMobile.formulaire.version')}
              rules={[{ required: true, message: t('applicationMobile.formulaire.versionObligatoire') }]}>
              <Input placeholder="1.4.0" />
            </Form.Item>
            <Form.Item name="notes" label={t('applicationMobile.formulaire.notes')}>
              <Input.TextArea rows={3} placeholder={t('applicationMobile.formulaire.notesExemple')} />
            </Form.Item>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}
              loading={envoiAndroid} style={{ background: VERT, borderColor: VERT }}>
              {t('applicationMobile.formulaire.publier')}
            </Button>
          </Form>
        </Card>

        <Card className="cb-card" style={{ flex: 1, minWidth: 360 }}
          title={<Space><AppleOutlined />iOS</Space>}>
          <Courante etat={ios} suffixe={ios.urlExterne && <Tag>{ios.urlExterne}</Tag>} />
          <Form form={formIos} layout="vertical" onFinish={publierIos}>
            <Form.Item name="url" label={t('applicationMobile.formulaire.lienIos')}
              rules={[{ required: true, message: t('applicationMobile.formulaire.urlObligatoire') }]}
              extra={t('applicationMobile.formulaire.lienIosAide')}>
              <Input placeholder="https://apps.apple.com/app/id..." />
            </Form.Item>
            <Form.Item name="version" label={t('applicationMobile.formulaire.version')}
              rules={[{ required: true, message: t('applicationMobile.formulaire.versionObligatoire') }]}>
              <Input placeholder="1.4.0" />
            </Form.Item>
            <Form.Item name="notes" label={t('applicationMobile.formulaire.notes')}>
              <Input.TextArea rows={3} />
            </Form.Item>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={envoiIos}>
              {t('applicationMobile.formulaire.publier')}
            </Button>
          </Form>
        </Card>
      </div>
    </Layout>
  );
}
